import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Play, Square, Check, AudioLines, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function VoicesPage() {
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [voices, setVoices] = useState([]);
  const [currentVoiceId, setCurrentVoiceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);
  const [audio, setAudio] = useState(null);
  const [confirmVoice, setConfirmVoice] = useState(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: agentsData }, { data: voicesData }] = await Promise.all([
          api.get("/voice-gallery/agents"),
          api.get("/voice-gallery"),
        ]);
        setAgents(agentsData);
        setVoices(voicesData);
        if (agentsData.length > 0) setAgentId(agentsData[0].id);
      } catch {
        toast.error("Failed to load voices");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!agentId) return;
    api.get(`/voice-gallery/current?assistant_id=${encodeURIComponent(agentId)}`)
      .then(({ data }) => setCurrentVoiceId(data.voice_id))
      .catch(() => setCurrentVoiceId(null));
  }, [agentId]);

  const stopAudio = () => {
    if (audio) { audio.pause(); audio.currentTime = 0; }
    setAudio(null);
    setPlaying(null);
  };

  const togglePlay = (v) => {
    if (playing === v.id) { stopAudio(); return; }
    stopAudio();
    const token = localStorage.getItem("cs_token");
    const a = new Audio(`${API}/voice-preview/${encodeURIComponent(v.id)}?token=${encodeURIComponent(token)}`);
    a.onended = () => { setPlaying(null); setAudio(null); };
    a.onerror = () => { toast.error("Preview unavailable"); setPlaying(null); setAudio(null); };
    a.play().catch(() => { toast.error("Preview unavailable"); setPlaying(null); setAudio(null); });
    setAudio(a);
    setPlaying(v.id);
  };

  const applyVoice = async () => {
    if (!confirmVoice || !agentId) return;
    setApplying(true);
    try {
      await api.post("/voice-gallery/select", { assistant_id: agentId, voice_id: confirmVoice.id });
      setCurrentVoiceId(confirmVoice.id);
      toast.success(`Voice updated to ${confirmVoice.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update voice");
    } finally {
      setApplying(false);
      setConfirmVoice(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="voices-page">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Call Swiper</p>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Voice Gallery</h1>
          <p className="text-sm text-muted-foreground mt-1">Preview and choose the voice your agent answers calls with.</p>
        </div>
        {agents.length > 1 && (
          <div className="w-full sm:w-64">
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Agent</label>
            <Select value={agentId} onValueChange={(v) => { stopAudio(); setAgentId(v); }}>
              <SelectTrigger data-testid="voice-agent-select"><SelectValue placeholder="Choose agent" /></SelectTrigger>
              <SelectContent>
                {agents.map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`voice-agent-option-${a.id}`}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-16 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading voices…
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {voices.map((v) => {
            const isCurrent = currentVoiceId === v.id;
            return (
              <Card key={v.id} data-testid={`voice-card-${v.id}`} className={`p-5 flex flex-col gap-3 transition-colors ${isCurrent ? "border-primary/60 bg-primary/[0.04]" : ""}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-base truncate" data-testid={`voice-name-${v.id}`}>{v.name}</div>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {v.gender && <Badge variant="outline" className="text-[10px] capitalize">{v.gender}</Badge>}
                      {v.accent && <Badge variant="outline" className="text-[10px]">{v.accent}</Badge>}
                    </div>
                  </div>
                  {isCurrent && (
                    <Badge className="shrink-0 bg-primary text-primary-foreground" data-testid={`voice-current-badge-${v.id}`}>
                      <Check className="w-3 h-3 mr-1" /> Current
                    </Badge>
                  )}
                </div>
                {v.description && <p className="text-xs text-muted-foreground line-clamp-2">{v.description}</p>}
                <div className="flex gap-2 mt-auto pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    disabled={!v.has_preview}
                    title={v.has_preview ? "Play preview" : "Preview not yet published for this voice"}
                    onClick={() => togglePlay(v)}
                    data-testid={`voice-play-btn-${v.id}`}
                  >
                    {playing === v.id ? <Square className="w-3.5 h-3.5 mr-1.5" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
                    {playing === v.id ? "Stop" : v.has_preview ? "Play" : "No preview"}
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1"
                    disabled={isCurrent}
                    onClick={() => { stopAudio(); setConfirmVoice(v); }}
                    data-testid={`voice-select-btn-${v.id}`}
                  >
                    <AudioLines className="w-3.5 h-3.5 mr-1.5" />
                    {isCurrent ? "Active" : "Use this voice"}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!confirmVoice} onOpenChange={(o) => !o && setConfirmVoice(null)}>
        <AlertDialogContent data-testid="voice-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Change voice to {confirmVoice?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This updates the live agent immediately. All new calls will be answered with this voice.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="voice-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={applyVoice} disabled={applying} data-testid="voice-confirm-apply">
              {applying ? "Updating…" : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
